'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { SectionHeading } from '@/components/frontend/home/section-heading';
import { FeaturesGrid } from '@/components/frontend/features/features-grid';
import { Typography } from '@/components/ui/typography';
import { Button } from '@/components/ui/button';
import { getLocalizedPath, getLocalizedPagePath } from '@/lib/locale-utils';
import { cn } from '@/lib/utils';

type FeatureItem = { id: string; title: string; description: string; icon?: string | null; category: string; hasDetailPage?: boolean; showOnHomePage?: boolean; slug?: string | null; locale?: string | null };

export function HomeFeatures({ initialFeatures }: { initialFeatures?: FeatureItem[] }) {
    const locale = useLocale();
    const t = useTranslations('home.mlmFeatures');
    const sectionRef = useScrollAnimation({ threshold: 0.15 });

    const content = {
        pillLabel: t('pillLabel'),
        title: t('title'),
        highlightedTitle: t('highlightedTitle'),
        subtitle: t('subtitle'),
        paragraph1: t('paragraph1'),
        paragraph2: t('paragraph2'),
        buttonText: t('buttonText'),
    };

    return (
        <SectionWrapper
            ref={sectionRef.ref}
            variant="default"
            padding="md"
            className="relative !overflow-visible"
        >
            <div className="relative z-10">
                <div className="grid lg:grid-cols-[40%_60%] items-start">
                    {/* Left Column - Sticky */}
                    <div className="lg:sticky lg:top-32 lg:self-start mb-10">
                        <div
                            className={cn(
                                'transition-all duration-700 md:pr-20',
                                sectionRef.isVisible
                                    ? 'opacity-100 translate-y-0'
                                    : 'opacity-0 translate-y-6'
                            )}
                        >
                            <SectionHeading
                                pill={content.pillLabel}
                                title={`${content.title} ${content.highlightedTitle}`}
                                subtitle={content.subtitle}
                                isVisible={sectionRef.isVisible}
                                align="left"
                                maxWidthClass="max-w-3xl"
                                className="!mb-4"
                            />
                            <Typography
                                variant="p"
                                className="mb-4 text-base text-gray-600 dark:text-gray-300 leading-relaxed"
                            >
                                {content.paragraph1}
                            </Typography>
                            <Typography
                                variant="p"
                                className="mb-6 text-base text-gray-600 dark:text-gray-300 leading-relaxed"
                            >
                                {content.paragraph2}
                            </Typography>
                            <Button
                                href={getLocalizedPagePath('mlm-software-features', locale)}
                                variant="primary"
                                size="md"
                            >
                                {content.buttonText}
                            </Button>
                        </div>
                    </div>

                    {/* Right Column - Scrollable Grid */}
                    <div
                        className="transition-all duration-700"
                        style={{ transitionDelay: '200ms' }}
                    >
                        <FeaturesGrid limit={8} className="grid-cols-1 md:grid-cols-2" showOnHomePageOnly={true} initialFeatures={initialFeatures} />
                    </div>
                </div>
            </div>
        </SectionWrapper>
    );
}

